import {
  useDiscountData,
  useExtendedBasketData,
} from "@chef/state-management/hooks";
import { sumByFn, sumByProperty } from "@chef/utils/array";
import {
  isMealboxProduct,
  isPickAndMixProduct,
  isStandaloneProduct,
} from "@chef/helpers";
import { CurrencyWrapper as Currency } from "@chef/smart-components";

import type { WeekYear } from "@chef/types/common";

import { intl } from "./WeekEditorConfirmOrderExperimental.Intl";

export const Summary = ({ week, year }: WeekYear) => {
  const { data: extendedBasket } = useExtendedBasketData({ week, year });
  const {
    discounts: {
      activeDiscounts: {
        permanentDiscounts: { nextPermanentDiscount },
      },
    },
  } = useDiscountData();
  const { selectedProducts, portions, deliveryFee } = extendedBasket;
  const meals = selectedProducts?.filter(isPickAndMixProduct);
  const standaloneProducts =
    selectedProducts?.filter(isStandaloneProduct) ?? [];
  const mealbox = selectedProducts?.find(isMealboxProduct);

  const numMeals = meals?.length ?? 0;
  const numStandaloneProducts = sumByProperty(standaloneProducts, "quantity");

  const mealboxPrice = mealbox?.price ?? 0;
  const standaloneProductsPrice = sumByFn(
    standaloneProducts,
    (p) => p.quantity * p.price,
  );

  const discount = nextPermanentDiscount?.amount;

  return (
    <div className="flex flex-col gap-4 [&_span]:text-grey-1 md:[&_span]:text-black md:p-4 mt-4 md:mt-0">
      <h1 className="hidden text-lg md:block">{intl.SUMMARY}</h1>

      <div className="flex flex-col md:gap-2">
        <div className="flex justify-between">
          <span>
            {numMeals} {intl.MEALS}, {portions} {intl.PORTIONS}
          </span>
          <Currency trimDecimal>{mealboxPrice}</Currency>
        </div>

        {!!numStandaloneProducts && (
          <div className="flex justify-between">
            <span>
              {numStandaloneProducts} {intl.ADDONS}
            </span>
            <Currency trimDecimal>{standaloneProductsPrice}</Currency>
          </div>
        )}

        {!!discount && (
          <div className="flex justify-between">
            <span>{intl.DISCOUNT}</span>
            {nextPermanentDiscount?.type === "%" ? (
              <span>{discount} %</span>
            ) : (
              <Currency trimDecimal>{discount}</Currency>
            )}
          </div>
        )}

        <div className="flex justify-between">
          <span>{intl.DELIVERY}</span>

          <Currency trimDecimal>{deliveryFee}</Currency>
        </div>

        <div className="flex justify-between pt-2 mt-2 border-t-grey-3 border-t-1.5 md:border-none md:mt-0 md:pt-0">
          <strong>{intl.TOTAL}</strong>

          <Currency trimDecimal emphasized className="!text-black">
            {mealboxPrice + standaloneProductsPrice + deliveryFee}
          </Currency>
        </div>
      </div>
    </div>
  );
};
